'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import UserContextMenu from './UserContextMenu';

const Header = () => {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, userProfile, loading, signOut, isAuthenticated } = useAuth();
  const router = useRouter();

  const navigationItems = [
    { label: 'Home', path: '/home', icon: 'HomeIcon' },
    { label: 'Contact', path: '/home#contact', icon: 'EnvelopeIcon' },
    { label: 'Create Plan', path: '/input-form', icon: 'PlusCircleIcon' },
    {
      label: 'Resources',
      icon: 'BookOpenIcon',
      isDropdown: true,
      dropdownItems: [
        { label: 'Blog', path: '/blog', icon: 'NewspaperIcon' },
        { label: 'Help Center', path: '/help-center', icon: 'QuestionMarkCircleIcon' },
        { label: 'Resource Library', path: '/library', icon: 'BookOpenIcon' }
      ]
    },
  ];

  const isActivePath = (path: string) => {
    if (path.includes('#')) return false;
    if (path === '/input-form') return pathname === '/input-form' || pathname === '/ai-plan';
    if (path === '/blog') return pathname === '/blog' || pathname?.startsWith('/blog/');
    if (path === '/help-center') return pathname === '/help-center';
    return pathname === path;
  };

  const handleLogout = async () => {
    await signOut();
    router.push('/home');
  };

  const DropdownMenu = ({ item }: { item: any }) => {
    const [isOpen, setIsOpen] = useState(false);
    const node = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (node.current?.contains(e.target as Node)) return;
        setIsOpen(false);
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
      <div ref={node} className="relative">
        <button
          onMouseEnter={() => setIsOpen(true)}
          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 cursor-pointer ${item.dropdownItems.some((sub: any) => isActivePath(sub.path)) ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground'
            }`}>
          <Icon name={item.icon as any} size={18} />
          <span>{item.label}</span>
          <Icon name="ChevronDownIcon" size={16} />
        </button>
        {isOpen && (
          <div
            onMouseLeave={() => setIsOpen(false)}
            className="absolute mt-2 w-48 bg-card border border-border rounded-md shadow-lg py-1 z-20">
            {item.dropdownItems.map((subItem: any) => (
              <Link
                key={subItem.path}
                href={subItem.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${isActivePath(subItem.path) ? 'text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}>
                <Icon name={subItem.icon as any} size={16} />
                <span>{subItem.label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="bg-background/80 border-b border-border sticky top-0 z-50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <Link href="/home" className="flex items-center space-x-2 flex-shrink-0">
            <img src="https://pzkbprtrxmmiyuuoiuhp.supabase.co/storage/v1/object/sign/MicroSpace%20Public/Images/logo.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV82YmU4ODRkZC0xYmM4LTQyZjYtYWVjMC03Y2M0ODFkZTJmN2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJNaWNyb1NwYWNlIFB1YmxpYy9JbWFnZXMvbG9nby5wbmciLCJpYXQiOjE3NjQyNDM2NDIsImV4cCI6MTkyMTkyMzY0Mn0.HOyoXf4wkmlah6laQc9F5m1yMhvlVrxKcUshuIikRnE" alt="MicroSpace Optimizer Logo" className="w-8 h-8" />
            <span className="text-xl font-heading font-semibold text-foreground">
              MicroSpace Optimizer
            </span>
          </Link>

          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => (
              item.isDropdown ? <DropdownMenu key={item.label} item={item} /> : (
                <Link
                  key={item.path}
                  href={item.path!}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${isActivePath(item.path!) ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground'
                    }`}>
                  <Icon name={item.icon as any} size={18} />
                  <span>{item.label}</span>
                </Link>
              )
            ))}
          </nav>

          <div className="flex items-center space-x-2">
            <div className="hidden md:block">
              <UserContextMenu
                user={userProfile ? { name: userProfile.name || 'User', email: userProfile.email || '' } : undefined}
                onLogout={handleLogout}
                isAuthenticated={isAuthenticated}
              />
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200">
              <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background">
            <nav className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                item.isDropdown ? (
                  <div key={item.label} className="space-y-1">
                    <span className="flex items-center space-x-3 px-3 py-3 text-base font-medium text-muted-foreground">
                      <Icon name={item.icon as any} size={20} />
                      <span>{item.label}</span>
                    </span>
                    <div className="pl-8 space-y-1">
                      {item.dropdownItems.map(subItem => (
                        <Link
                          key={subItem.path}
                          href={subItem.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors duration-200 ${isActivePath(subItem.path) ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}>
                          <Icon name={subItem.icon as any} size={20} />
                          <span>{subItem.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    href={item.path!}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors duration-200 ${isActivePath(item.path!) ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}>
                    <Icon name={item.icon as any} size={20} />
                    <span>{item.label}</span>
                  </Link>
                )
              ))}

              <div className="border-t border-border pt-4 mt-4">
                {isAuthenticated && userProfile ? (
                  <>
                    <Link href="/account" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-foreground hover:bg-muted">
                      <Icon name="UserIcon" size={20} />
                      <span>{userProfile.name || 'Account'}</span>
                    </Link>
                    <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-destructive hover:bg-destructive/10">
                      <Icon name="ArrowRightOnRectangleIcon" size={20} />
                      <span>Log Out</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link href="/login" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-foreground hover:bg-muted">
                      <Icon name="ArrowLeftOnRectangleIcon" size={20} />
                      <span>Sign In</span>
                    </Link>
                    <Link href="/register" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-3 px-3 py-3 bg-primary text-primary-foreground rounded-md text-base font-medium hover:bg-primary/90">
                      <Icon name="UserPlusIcon" size={20} />
                      <span>Get Started</span>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;